<template>
  <div class="w-full">
    <Head title="Alla recept" />
    <!-- Hero / Header -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16">
      <div class="flex flex-col items-center gap-5 md:gap-6">
        <h1
          class="font-[Poppins] font-extrabold text-[#191c1e] tracking-[-2px] text-center m-0"
          style="font-size: clamp(36px, 8vw, 60px)"
        >
          Alla recept
        </h1>
        <p
          class="text-[18px] text-[#3c4947] text-center max-w-[672px] leading-[28px]"
        >
          Här har jag samlat alla mina favoriter. Från snabba vardagsräddare
          till de där rätterna som får ta hela söndagen. Vad är du sugen på
          idag?
        </p>

        <!-- Search -->
        <div class="relative w-full max-w-[672px]">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Sök bland hundratals recept..."
            class="w-full py-4 pl-12 pr-4 rounded-xl outline-none border border-[#e6e8ea] bg-[#f2f4f6] text-[16px] text-[#6c7a77]"
            @input="debouncedSearch"
          />
          <Search
            class="absolute left-4 top-1/2 -translate-y-1/2 text-[#6c7a77]"
            :size="18"
          />
        </div>

        <!-- Filter pills -->
        <div class="flex flex-wrap justify-center gap-2 items-center">
          <button
            class="px-5 py-2 rounded-full transition-colors text-[14px] border-none cursor-pointer"
            :class="
              activeCategory === null
                ? 'bg-teal-500 text-white font-bold'
                : 'bg-[#e6e8ea] text-[#3c4947] font-normal'
            "
            @click="filterByCategory(null)"
          >
            Alla
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="px-5 py-2 rounded-full transition-colors text-[14px] border-none cursor-pointer"
            :class="
              activeCategory === cat.id
                ? 'bg-teal-500 text-white font-bold'
                : 'bg-[#e6e8ea] text-[#3c4947] font-normal'
            "
            @click="filterByCategory(cat.id)"
          >
            {{ cat.name }}
          </button>
          <a
            v-if="currentUser && currentUser.admin"
            :href="categoriesPath"
            class="px-3 py-2 rounded-full text-[13px] font-bold text-teal-600 border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors whitespace-nowrap"
            >+ Hantera kategorier</a
          >
        </div>
      </div>
    </section>

    <!-- Recipe Grid — First batch (up to 8) -->
    <section
      v-if="allRecipes.length > 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-8 md:pb-10"
    >
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <RecipeCard
          v-for="recipe in firstBatch"
          :key="recipe.id"
          :recipe="recipe"
          :current-user="currentUser"
        />
      </div>
    </section>

    <!-- Empty state -->
    <section
      v-if="allRecipes.length === 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16"
    >
      <p class="text-center text-[#6c7a77] text-lg">Hittade inga recept</p>
    </section>

    <!-- Banner — only if 9+ recipes -->
    <section
      v-if="showBanner"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-8 md:pb-10"
    >
      <Link :href="bannerSlotRecipe.path" class="block no-underline">
        <div
          class="relative overflow-hidden rounded-[24px] md:rounded-[32px]"
          style="height: clamp(240px, 40vw, 340px)"
        >
          <img
            :src="bannerSlotRecipe.cover_image?.xlarge"
            :alt="bannerSlotRecipe.title"
            class="w-full h-full object-cover"
          />
          <div
            class="absolute inset-0 flex flex-col justify-center p-6 md:p-12"
            style="
              background: linear-gradient(
                to right,
                rgba(0, 0, 0, 0.7) 0%,
                rgba(0, 0, 0, 0.2) 60%,
                rgba(0, 0, 0, 0) 100%
              );
            "
          >
            <h2
              class="font-[Poppins] font-extrabold text-white m-0 mb-3 leading-[1.1]"
              style="font-size: clamp(28px, 5vw, 48px)"
            >
              {{ bannerSlotRecipe.title }}
            </h2>
            <p
              v-if="bannerSlotRecipe.ingress"
              class="hidden sm:block text-[16px] text-white/90 max-w-[320px] mb-5"
            >
              {{ bannerSlotRecipe.ingress }}
            </p>
            <span
              class="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-500 text-white font-bold text-[14px] w-fit"
            >
              Visa receptet
            </span>
          </div>
        </div>
      </Link>
    </section>

    <!-- Recipe Grid — Remaining recipes -->
    <section
      v-if="remainingRecipes.length > 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-8 md:pb-10"
    >
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <RecipeCard
          v-for="recipe in remainingRecipes"
          :key="recipe.id"
          :recipe="recipe"
          :current-user="currentUser"
        />
      </div>
    </section>

    <!-- Load more button — only if 13+ total recipes -->
    <section
      v-if="showLoadMore"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16 md:pb-20 flex justify-center"
    >
      <button
        class="px-8 py-3 rounded-xl border-2 border-[#e6e8ea] bg-transparent font-bold text-[15px] text-[#191c1e] cursor-pointer transition-colors hover:bg-[#f2f4f6]"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? "Laddar..." : "Visa fler recept" }}
      </button>
    </section>

    <!-- Admin: add new recipe -->
    <section
      v-if="currentUser && currentUser.admin"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-8 flex justify-center"
    >
      <a
        :href="newRecipePath"
        class="flex items-center justify-center w-16 h-16 rounded-full border-2 border-dashed border-gray-300 hover:bg-gray-100 transition-colors"
      >
        <PhPlus class="w-6 h-6 text-gray-400" />
      </a>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Head, Link, router } from "@inertiajs/vue3";
import { Search } from "lucide-vue-next";
import { PhPlus } from "@phosphor-icons/vue";
import AppLayout from "@/layouts/AppLayout.vue";
import RecipeCard from "@/components/recipe/RecipeCard.vue";

const props = defineProps({
  recipes: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  bannerRecipe: { type: Object, default: null },
  currentUser: { type: Object, default: null },
  csrfToken: { type: String, default: "" },
  newRecipePath: { type: String, default: "" },
  recipesPath: { type: String, default: "/recept" },
  categoriesPath: { type: String, default: "/kategorier" },
  activeSearch: { type: String, default: "" },
  activeCategory: { type: [Number, String], default: null },
  pagy: { type: Object, default: () => ({ pages: 1, page: 1 }) },
});

defineOptions({
  layout: (h, page) =>
    h(AppLayout, { bodyMargin: "mt-20 lg:mt-50" }, () => page),
});

// --- State ---
const allRecipes = ref([...props.recipes]);
const activeCategory = ref(
  props.activeCategory ? Number(props.activeCategory) : null,
);
const searchQuery = ref(props.activeSearch || "");
const loading = ref(false);
const hasMore = ref(!!props.pagy.next);
const isLoadingMore = ref(false);

// --- Watch for Inertia prop changes ---
watch(
  () => props.recipes,
  (newRecipes) => {
    if (isLoadingMore.value) {
      allRecipes.value = [...allRecipes.value, ...newRecipes];
      isLoadingMore.value = false;
    } else {
      allRecipes.value = [...newRecipes];
    }
    hasMore.value = !!props.pagy.next;
    loading.value = false;
  },
);

// --- Computed ---
const totalCount = computed(() => allRecipes.value.length);
const bannerSlotRecipe = computed(() => allRecipes.value[8] ?? null);
const showBanner = computed(
  () => !!bannerSlotRecipe.value?.cover_image?.xlarge,
);
const firstBatch = computed(() => allRecipes.value.slice(0, 8));
const remainingRecipes = computed(() =>
  showBanner.value ? allRecipes.value.slice(9) : allRecipes.value.slice(8),
);
const showLoadMore = computed(() => hasMore.value);

// --- Methods ---
let searchTimeout = null;
function debouncedSearch() {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    navigateWithFilters();
  }, 300);
}

function filterByCategory(categoryName) {
  activeCategory.value = categoryName;
  navigateWithFilters();
}

function navigateWithFilters() {
  const params = {};
  if (searchQuery.value) params.search = searchQuery.value;
  if (activeCategory.value) params.category = activeCategory.value;

  isLoadingMore.value = false;
  router.get(props.recipesPath, params, {
    preserveState: true,
    preserveScroll: false,
  });
}

function loadMore() {
  if (!props.pagy.next) return;
  loading.value = true;
  isLoadingMore.value = true;

  const params = { page: props.pagy.next };
  if (searchQuery.value) params.search = searchQuery.value;
  if (activeCategory.value) params.category = activeCategory.value;

  router.get(props.recipesPath, params, {
    preserveState: true,
    preserveScroll: true,
  });
}
</script>
